/**
 * Executive Day Briefing — Firestore subscription + staff refresh callable.
 */

import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import {
  ExecutiveDayBriefing,
  executiveDayBriefingDocId,
  todayBriefingDateKey,
} from '../types';

const regionFunctions = () => getFunctions(undefined, 'us-central1');

function callableErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const msg = String((err as { message: string }).message);
    if (msg && msg !== 'INTERNAL') return msg;
  }
  if (err && typeof err === 'object' && 'details' in err && (err as { details?: unknown }).details) {
    return String((err as { details: unknown }).details);
  }
  return 'Briefing generation failed. Try again or contact support.';
}

function toDate(v: unknown): Date {
  if (!v) return new Date(0);
  if (v instanceof Date) return v;
  if (typeof v === 'object' && v !== null && 'toDate' in v && typeof (v as { toDate: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? new Date(0) : d;
  }
  return new Date(0);
}

function mapBriefing(id: string, d: Record<string, unknown>): ExecutiveDayBriefing {
  return {
    id,
    ownerUid: String(d.ownerUid || ''),
    dateKey: String(d.dateKey || ''),
    allowedUids: Array.isArray(d.allowedUids) ? d.allowedUids as string[] : [],
    generatedAt: toDate(d.generatedAt),
    generatedBy: d.generatedBy === 'crm' ? 'crm' : 'agent',
    narrative: String(d.narrative || ''),
    priorities: Array.isArray(d.priorities) ? d.priorities as ExecutiveDayBriefing['priorities'] : [],
    dismissedPriorityKeys: Array.isArray(d.dismissedPriorityKeys)
      ? d.dismissedPriorityKeys as string[]
      : [],
    sections: d.sections as ExecutiveDayBriefing['sections'],
    contextSnapshot: d.contextSnapshot as Record<string, unknown> | undefined,
  };
}

export { todayBriefingDateKey, executiveDayBriefingDocId };

export function subscribeDayBriefing(
  ownerUid: string,
  dateKey: string,
  onData: (briefing: ExecutiveDayBriefing | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const ref = doc(db, 'executiveDayBriefings', executiveDayBriefingDocId(ownerUid, dateKey));
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onData(null);
        return;
      }
      onData(mapBriefing(snap.id, snap.data() as Record<string, unknown>));
    },
    (err) => onError?.(err),
  );
}

export async function refreshDayBriefing(
  ownerUid: string,
  options?: { dateKey?: string; timeZone?: string },
): Promise<ExecutiveDayBriefing | null> {
  try {
    const res = await httpsCallable(regionFunctions(), 'getExecutiveDayBriefing')({
      ownerUid,
      dateKey: options?.dateKey || todayBriefingDateKey(options?.timeZone),
      timeZone: options?.timeZone,
      refresh: true,
    });
    const data = res.data as { briefing?: Record<string, unknown> | null };
    const b = data?.briefing;
    if (!b) return null;
    return mapBriefing(
      executiveDayBriefingDocId(ownerUid, String(b.dateKey || options?.dateKey || todayBriefingDateKey())),
      b,
    );
  } catch (err) {
    throw new Error(callableErrorMessage(err));
  }
}

export async function fetchDayBriefing(
  ownerUid: string,
  options?: { dateKey?: string; timeZone?: string },
): Promise<ExecutiveDayBriefing | null> {
  const res = await httpsCallable(regionFunctions(), 'getExecutiveDayBriefing')({
    ownerUid,
    dateKey: options?.dateKey || todayBriefingDateKey(options?.timeZone),
    timeZone: options?.timeZone,
    refresh: false,
  });
  const data = res.data as { briefing?: Record<string, unknown> | null };
  const b = data?.briefing;
  if (!b) return null;
  return mapBriefing(
    executiveDayBriefingDocId(ownerUid, String(b.dateKey || options?.dateKey || todayBriefingDateKey())),
    b,
  );
}

export async function dismissBriefingPriority(
  ownerUid: string,
  priorityKey: string,
  options?: { dateKey?: string },
): Promise<void> {
  await httpsCallable(regionFunctions(), 'dismissBriefingPriority')({
    ownerUid,
    dateKey: options?.dateKey || todayBriefingDateKey(),
    priorityKey,
  });
}
